import Drawer from '@material-ui/core/Drawer';
import { CreationTaskDrawer } from 'features/daily-plan';
import {
  ProfileAccountableBar,
  ProfileHeaderActions,
  ProfilePageLayout,
  ProfileSummary,
  ProfileTabs
} from 'features/inventory-profiles/components/shared';
import {
  COMMON_TAB_IDS,
  HIRING_AUTHORITY_DRAWERS,
  HIRING_AUTHORITY_PROFILE_CONFIG,
  HIRING_AUTHORITY_TAB_IDS
} from 'features/inventory-profiles/constants';
import {
  useHiringAuthorityProfile,
  useProfileAccountability,
  useProfileDrawer,
  useTabRefresh
} from 'features/inventory-profiles/hooks';
import { ProfileRestrictionDrawer, useProfileRestrictions } from 'features/profile-restrictions';
import { useSearchProjectInProfiles } from 'hooks/searchProject';
import { When } from 'UI/components/atoms';
import LinkCandidate from 'UI/components/organisms/HiringAuthorities/Candidate/LinkCandidate';
import HiringAuthorityDrawer from 'UI/components/organisms/HiringAuthorityDrawer';
import ToggleProfileModeButton from 'UI/components/organisms/inventoryProfiles/ToggleProfileModeButton';
import { handleEntityEditionCompleted } from 'UI/components/organisms/inventoryProfiles/utils';
import SendoutManager from 'UI/components/organisms/sendouts';
import { Endpoints } from 'UI/constants/endpoints';
import { EntityType, SearchProjectEntities } from 'UI/constants/entityTypes';

import { HIRING_AUTHORITY_TABS_CONFIG } from './HiringAuthorityProfileV2.constants';
import {
  useHiringAuthorityNavigation,
  useHiringAuthorityPermissions,
  useUnlinkCandidate
} from './HiringAuthorityProfileV2.hooks';
import { buildHiringAuthorityTabs } from './HiringAuthorityProfileV2.tabs';
import {
  buildHiringAuthorityMenuItems,
  formatHiringAuthorityResponse
} from './HiringAuthorityProfileV2.utils';

export const HiringAuthorityProfileV2 = ({
  enableUrlNavigation = true,
  id,
  layoutClassName,
  onEditionCompleted,
  profileModeProps
}) => {
  const { hiringAuthority, isLoading, error, refresh, reload } = useHiringAuthorityProfile(id);
  const isProfileReady = !isLoading && !error && Boolean(hiringAuthority.id);
  const formattedHiringAuthority = formatHiringAuthorityResponse(hiringAuthority);

  const permissions = useHiringAuthorityPermissions(hiringAuthority);
  const restrictions = useProfileRestrictions({
    canRequestDoNotContact: permissions.canRequestDoNotContact,
    canRequestDoNotEngage: permissions.canRequestDoNotEngage,
    entityTypeId: HIRING_AUTHORITY_PROFILE_CONFIG.entityTypeId,
    profileStatusId: hiringAuthority.status?.id
  });

  const { refreshTab, getTabVersion } = useTabRefresh();
  const {
    drawer,
    drawerItemId,
    openDrawer,
    openItemDrawer,
    closeDrawer,
    closeAndRefresh,
    closeAndRefreshTab
  } = useProfileDrawer({ onChanged: refresh, onTabChanged: refreshTab });
  const assignment = useProfileAccountability({
    canEdit: permissions.canEdit,
    entity: HIRING_AUTHORITY_PROFILE_CONFIG,
    profile: hiringAuthority
  });

  const { SearchProjectForms, SearchProjectsMenuItems, searchProjectTabPropsDefinition } =
    useSearchProjectInProfiles({
      endpoint: `${HIRING_AUTHORITY_PROFILE_CONFIG.endpoint}/${id}`,
      entity: SearchProjectEntities.HiringAuthority,
      profile: { id: hiringAuthority.id, name: formattedHiringAuthority.header.title },
      tabKey: COMMON_TAB_IDS.SearchProjects,
      triggerTabRefresh: refreshTab,
      shouldRedirectToSearchProjectPreview: enableUrlNavigation
    });

  const tabs = buildHiringAuthorityTabs({
    hiringAuthority,
    hiringAuthorityName: formattedHiringAuthority.header.title,
    isLoading,
    openItemDrawer,
    permissions,
    refresh,
    refreshTab,
    searchProjectsTab: searchProjectTabPropsDefinition
  });

  const navigation = useHiringAuthorityNavigation({ id });
  const unlinkCandidate = useUnlinkCandidate({
    candidateId: hiringAuthority.relation?.candidate?.id,
    hiringName: hiringAuthority.full_name,
    id,
    refresh
  });
  const menuItems = buildHiringAuthorityMenuItems({
    permissions,
    searchProjectMenuItems: SearchProjectsMenuItems,
    restrictionMenuItems: restrictions.menuItems,
    openDrawer,
    onAddCandidate: navigation.addCandidate,
    onUnlinkCandidate: unlinkCandidate
  });

  return (
    <>
      <ProfilePageLayout
        header={formattedHiringAuthority.header}
        enableUrlNavigation={enableUrlNavigation}
        layoutClassName={layoutClassName}
        hasHeaderActions={permissions.hasCandidate}
        actions={
          <ProfileHeaderActions menuItems={menuItems}>
            <When condition={permissions.hasCandidate}>
              <ToggleProfileModeButton
                mode={EntityType.HiringAuthority}
                results={hiringAuthority}
                {...profileModeProps}
              />
            </When>
          </ProfileHeaderActions>
        }
        subheader={
          <>
            <ProfileSummary
              company={formattedHiringAuthority.summary.company}
              location={formattedHiringAuthority.summary.location}
              status={formattedHiringAuthority.status}
              created={formattedHiringAuthority.summary.created}
              details={formattedHiringAuthority.summary.details}
            />
            <ProfileAccountableBar assignment={assignment} />
          </>
        }
        isLoading={isLoading}
        error={error}
        onRetry={reload}
      >
        <ProfileTabs
          tabs={tabs}
          getTabVersion={getTabVersion}
          enableUrlNavigation={enableUrlNavigation}
          organizationStorageKey={HIRING_AUTHORITY_PROFILE_CONFIG.tabsStorageKey}
          organizationSubheader={HIRING_AUTHORITY_PROFILE_CONFIG.tabsSubheader}
        />
      </ProfilePageLayout>

      <When
        condition={
          isProfileReady && drawer === HIRING_AUTHORITY_DRAWERS.Edit && permissions.canEdit
        }
      >
        <HiringAuthorityDrawer
          type={EntityType.Company}
          isEditing
          companyId={hiringAuthority.company_id}
          hiringAuthority={hiringAuthority}
          endpoint={`${Endpoints.Companies}/${hiringAuthority.company_id}/${Endpoints.HiringAuthorities}/${hiringAuthority.id}`}
          onHACompleted={handleEntityEditionCompleted({
            handleEditCompleted: closeAndRefresh,
            enableUrlNavigation,
            onEditionCompleted
          })}
          onHAClosed={closeDrawer}
        />
      </When>
      <When
        condition={drawer === HIRING_AUTHORITY_DRAWERS.LinkCandidate && !permissions.hasCandidate}
      >
        <LinkCandidate
          hiringAuthorityId={id}
          onClose={closeDrawer}
          onCompleted={navigation.openLinkedCandidate}
        />
      </When>
      <When condition={isProfileReady && drawer === HIRING_AUTHORITY_DRAWERS.Sendout}>
        <Drawer open onClose={closeDrawer}>
          <SendoutManager
            id={drawerItemId}
            onEdit={closeAndRefreshTab(HIRING_AUTHORITY_TAB_IDS.Sendouts)}
            onDelete={closeAndRefreshTab(HIRING_AUTHORITY_TAB_IDS.Sendouts)}
            onClose={closeDrawer}
            onNavigate={navigation.navigateFromSendout}
          />
        </Drawer>
      </When>
      <When condition={drawer === HIRING_AUTHORITY_DRAWERS.Task && permissions.canUseDailyPlan}>
        <CreationTaskDrawer
          allowedTaskTypes={
            HIRING_AUTHORITY_TABS_CONFIG[HIRING_AUTHORITY_TAB_IDS.ActionPlan].allowedTaskTypes
          }
          onClose={closeDrawer}
          entityData={{
            id: hiringAuthority.id,
            name: hiringAuthority.full_name,
            companyName: hiringAuthority.company?.name
          }}
          entityType={EntityType.HiringAuthority}
          onTaskCreated={navigation.openDailyPlan}
        />
      </When>
      <When condition={restrictions.drawerProps.open}>
        <ProfileRestrictionDrawer
          entityId={id}
          entityTypeId={HIRING_AUTHORITY_PROFILE_CONFIG.entityTypeId}
          onClose={restrictions.drawerProps.onClose}
          open={restrictions.drawerProps.open}
          selectedRestrictionType={restrictions.drawerProps.selectedRestrictionType}
        />
      </When>
      <SearchProjectForms />
    </>
  );
};
